// =========================
// AcceptTerms — PLATFORM-002 / ADR-058 (clickwrap enforcement)
//
// The affirmative-acceptance interstitial shown to any authenticated user who
// has not accepted the CURRENT Terms of Service + Privacy Policy. Rendered by
// ProtectedRoute in place of the app, so it uniformly covers email/password,
// Google OAuth, invite onboarding, existing users, AND re-acceptance after a
// version bump — a single enforcement point, not a checkbox bolted onto each
// auth form.
//
// UX contract: a required checkbox, links to the Terms and the Privacy Policy,
// and a Continue button that stays disabled until the box is checked. On accept
// it records acceptance server-side (lib/legalAcceptance) then calls onAccepted
// (SubscriptionContext.refreshProfile) so the gate re-evaluates and the app
// renders. Design-system tokens only (no hex).
// =========================

import React, { useState } from 'react';
import { acceptLegal } from '../lib/legalAcceptance';

export default function AcceptTerms({ onAccepted }) {
  const [checked, setChecked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  async function handleContinue() {
    if (!checked || busy) return;
    setBusy(true);
    setError('');
    const { error: acceptError } = await acceptLegal();
    if (acceptError) {
      setError(acceptError.message || 'Could not record your acceptance. Please try again.');
      setBusy(false);
      return;
    }
    // Success — ask the app to re-fetch the profile so the gate clears.
    if (typeof onAccepted === 'function') onAccepted();
    // Leave `busy` true; the gate will swap this view for the app on refresh.
  }

  const linkStyle = { color: 'var(--color-teal)', fontWeight: 'var(--weight-semibold)' };

  return (
    <div
      data-testid="accept-terms-gate"
      style={{
        minHeight: '100vh',
        background: 'var(--color-navy)',
        color: 'var(--color-white)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 'var(--space-6)',
        fontFamily: 'var(--font-body)',
      }}
    >
      <main
        className="card"
        style={{ maxWidth: 560, width: '100%', display: 'grid', gap: 'var(--space-4)', padding: 'var(--space-6)' }}
      >
        <header style={{ display: 'grid', gap: 'var(--space-2)' }}>
          <h1 style={{ margin: 0, fontFamily: 'var(--font-display)', fontSize: 'var(--text-2xl)', fontWeight: 'var(--weight-black)' }}>
            Before you continue
          </h1>
          <p style={{ margin: 0, color: 'var(--color-steel-lt)', lineHeight: 'var(--leading-normal)' }}>
            To use Rentava, please review and accept our Terms of Service and Privacy Policy. Rentava provides
            opportunity intelligence, not investment advice — you make the final decision.
          </p>
        </header>

        {error ? (
          <p data-testid="accept-terms-error" role="alert" style={{ margin: 0, color: 'var(--color-danger-lt)' }}>
            {error}
          </p>
        ) : null}

        <label
          htmlFor="accept-terms-checkbox"
          style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'flex-start', cursor: 'pointer' }}
        >
          <input
            id="accept-terms-checkbox"
            type="checkbox"
            checked={checked}
            onChange={(e) => setChecked(e.target.checked)}
            disabled={busy}
            style={{ marginTop: '4px' }}
          />
          <span style={{ color: 'var(--color-steel-lt)' }}>
            I have read and agree to the{' '}
            <a href="/terms" target="_blank" rel="noopener noreferrer" style={linkStyle}>
              Terms of Service
            </a>{' '}
            and{' '}
            <a href="/privacy" target="_blank" rel="noopener noreferrer" style={linkStyle}>
              Privacy Policy
            </a>
            .
          </span>
        </label>

        <button
          type="button"
          className="btn btn-primary btn-lg"
          onClick={handleContinue}
          disabled={!checked || busy}
        >
          {busy ? 'Saving…' : 'Continue'}
        </button>
      </main>
    </div>
  );
}
